import type { ReactNode } from 'react';
import { useState } from 'react';
import type { DerivedProfileSummary, StatementCheck, UpcomingPayout } from '@clarity/contract';
import { STATEMENT_TOLERANCE_PCT } from '@clarity/contract';
import { dayMonth, money, num, pct, periodDates, shortDate } from '../format.js';
import { upcomingNotes } from '../upcoming-notes.js';
import { adsLineLabel } from '../balance-ads.js';
import { SummaryGroupHead } from '../store/shared.js';
import { humanizeCareemCategory } from '../careem-labels.js';

/**
 * The payouts panel, grouped by the month Talabat PAID (clarity-reconciliation /
 * clarity-daily-update). EXTRACTED verbatim from the onboarding Step-3 derived
 * summary so BOTH the onboarding wizard and the standalone Payouts page render
 * the SAME month-by-month settlement story (grouping, statement match sentence,
 * opening/closing balances, estimated balance) — one definition of the logic,
 * two consumers. It is driven entirely by `DerivedProfileSummary.payouts` +
 * `estimatedBalance` + the store currency; it holds no component state, so it
 * drops into either host unchanged.
 */

/** `Sep 2026` for a statement month (pinned to UTC, like the period windows). */
export function monthLabel(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/**
 * A month fails when the payouts Talabat paid in it don't add up to the "paid"
 * the statement reports (beyond the tolerance), or the statement's own balance
 * lines don't add up. A month that can't be judged yet (no paid dates, or the
 * statement was stored without its balance lines) is not a failure.
 */
export function checkFails(c: StatementCheck): boolean {
  return c.payouts.withinTolerance === false || (c.cashFlow !== null && !c.cashFlow.adds);
}

export type PayoutRowData = DerivedProfileSummary['payouts']['rows'][number];

/**
 * The headline estimate of what Talabat still owes, shown at the top of the
 * payouts/money surface (clarity-daily-update Req 5.1–5.4). It reads the SAME
 * latest closing-balance + "paid later" reasoning the per-month blocks below use
 * (computed in `core`, carried on the summary), so the headline and the months
 * can never contradict. It is always a best-effort ESTIMATE: the wording and the
 * as-of month make that explicit, so it is never read as a settled, Talabat-
 * confirmed figure (Req 5.4). When the data can't support an estimate it says so
 * plainly WITH the reason — never a zero or a blank (Req 5.3).
 *
 * English-only strings, matching the surrounding payouts panel (no `useI18n`);
 * teal-glass, RTL-safe (logical properties + the shared `money`/`monthLabel`
 * locale-aware helpers).
 */
export function EstimatedBalanceCard({
  estimate,
  currency,
  platformLabel = 'Talabat',
}: {
  estimate: DerivedProfileSummary['estimatedBalance'];
  currency: string | undefined;
  /** The owning platform's display name (e.g. 'Careem'); defaults to 'Talabat'. */
  platformLabel?: string;
}) {
  const fmt = (n: number) => money(n, currency);
  // The as-of MONTH is kept for the stale-statement copy (unconfirmed names the
  // month). But the HEADLINE freshness is the exact window end (today/yesterday),
  // and the statement period-end date shows on the "confirmed" line — not the
  // month. EXACT dates, matching the dashboard card.
  const asOfMonth = estimate.asOf ? monthLabel(estimate.asOf) : null;
  const asOfDate = estimate.asOf ? shortDate(estimate.asOf) : null;
  const windowEndDate = estimate.windowEnd ? shortDate(estimate.windowEnd) : null;
  // How fresh the whole estimate is: the window end when there's a window, else
  // the statement date (the only anchor).
  const freshDate = windowEndDate ?? asOfDate;

  // The post-statement window terms (orders earned + ads spent SINCE the latest
  // statement). Older payloads omit them (defaulted to 0 on the wire), so the
  // headline falls back to the confirmed closing — unchanged behavior.
  const confirmed = estimate.confirmedOwed;
  const unpaidOrders = estimate.unpaidOrdersEstimate ?? 0;
  const adsSince = estimate.adsSinceStatement ?? 0;
  const hasWindow = unpaidOrders > 0.005 || adsSince > 0.005;
  const lastPayment = estimate.lastPayment ?? null;
  const breakdown = estimate.breakdown ?? { orders: [], ads: [] };
  const upcoming = estimate.upcomingPayout ?? null;
  // The headline is the full "money still coming": confirmed + since-estimate.
  // Fall back to confirmed when the producer didn't send a total (old payload).
  const headline =
    estimate.totalEstimatedOwed > 0.005 ? estimate.totalEstimatedOwed : confirmed;

  const owed = estimate.state === 'available' && headline > 0.005;

  // The four honest outcomes (clarity-daily-update Req 5.2/5.3/5.3a):
  //  - `owed`        — available + a positive headline (money still coming);
  //  - `clear`       — available + a GENUINE, data-backed zero → "settled everything";
  //  - `unconfirmed` — reconciled to ~0 but too stale to assert a zero (Req 4.2c):
  //                    we must NOT say "settled", we say we can't confirm it;
  //  - `unavailable` — couldn't be computed at all → the reason, plainly.
  const state: 'owed' | 'clear' | 'unconfirmed' | 'unavailable' = owed
    ? 'owed'
    : estimate.state === 'unavailable'
      ? 'unavailable'
      : estimate.state === 'unconfirmed'
        ? 'unconfirmed'
        : 'clear';

  // The copy for the non-owed branches. `unconfirmed` names the stale month and
  // the missing sync and never reads as settled (Req 5.3a); `unavailable` shows
  // its reason (Req 5.3); `clear` is the confident settled-zero line, shown ONLY
  // for a genuine available zero.
  const reason =
    state === 'unavailable'
      ? (estimate.reason ?? 'Not enough statement data to estimate yet.')
      : state === 'unconfirmed'
        ? asOfMonth
          ? `Can't confirm the balance — the latest statement Clarity has is from ${asOfMonth} and no newer payouts have synced, so this isn't a confirmed zero.`
          : "Can't confirm the balance — Clarity hasn't pulled a recent enough statement or payout to be sure, so this isn't a confirmed zero."
        : `${platformLabel} has settled everything Clarity can see — nothing outstanding right now.`;

  // Show the post-statement breakdown whenever a window exists — including the
  // `clear` (settled, headline floored to 0) case, so the ads that were spent
  // AND subtracted are VISIBLE rather than silently netted away. Only
  // `unavailable` / `unconfirmed` (no reconciled window to decompose) skip it.
  const showParts = hasWindow && (state === 'owed' || state === 'clear');

  return (
    <section
      className="payouts__estimate"
      data-state={state}
      aria-label={`Estimated balance owed by ${platformLabel}`}
    >
      <SummaryGroupHead title={`Estimated balance owed by ${platformLabel}`} />
      {owed ? (
        <p className="payouts__estimate-line">
          <span>
            {platformLabel} still owes you{' '}
            <span className="payouts__estimate-approx" aria-hidden="true">
              ≈
            </span>
          </span>
          <span className="num payouts__estimate-amount">{fmt(headline)}</span>
        </p>
      ) : (
        <p className="payouts__estimate-reason">{reason}</p>
      )}
      {showParts && (
        // The composition of the headline, so the soft "since" part is never
        // mistaken for the hard, reconciled closing balance — AND so the ads
        // deduction is always visible, even when it nets the headline to zero.
        <dl className="payouts__estimate-parts">
          <div className="payouts__estimate-part">
            <dt>Confirmed by last statement{asOfMonth ? <> ({asOfMonth})</> : null}</dt>
            <dd className="num">{fmt(confirmed)}</dd>
          </div>
          {unpaidOrders > 0.005 && (
            <div className="payouts__estimate-part">
              <dt>
                Earned since, not yet paid
                {asOfDate && windowEndDate ? (
                  <span className="payouts__estimate-range"> ({asOfDate} → {windowEndDate})</span>
                ) : null}
              </dt>
              <dd className="num">
                <span className="payouts__estimate-approx" aria-hidden="true">
                  ≈
                </span>{' '}
                {fmt(unpaidOrders)}
              </dd>
            </div>
          )}
          {adsSince > 0.005 && (
            <div className="payouts__estimate-part">
              <dt>Less ads spent since</dt>
              <dd className="num">−{fmt(adsSince)}</dd>
            </div>
          )}
        </dl>
      )}
      {state === 'clear' && adsSince > 0.005 && (
        // Make the netting explicit: a bare "settled" hides that ads WERE counted.
        <p className="payouts__estimate-note muted">
          Ads spent since the last statement ({fmt(adsSince)}) are accounted for — they net the
          balance to zero here.
        </p>
      )}
      {upcoming !== null && <UpcomingPayoutLine upcoming={upcoming} currency={currency} />}
      {lastPayment !== null && (
        // The last payment Talabat actually made — date, amount, AND the earning
        // period it covered — distinct from the still-owed estimate.
        <p className="payouts__estimate-lastpay">
          <span>
            Last payment · {shortDate(lastPayment.paidAt)}
            <span className="payouts__estimate-range">
              {' '}· for {shortDate(lastPayment.periodStart)} → {shortDate(lastPayment.periodEnd)}
            </span>
          </span>
          <span className="num payouts__estimate-lastpay-amt">{fmt(lastPayment.amount)}</span>
        </p>
      )}
      {showParts && (breakdown.orders.length > 0 || breakdown.ads.length > 0) && (
        <PayoutBalanceDetails breakdown={breakdown} currency={currency} />
      )}
      <p className="payouts__estimate-note muted">
        {owed ? (
          hasWindow ? (
            <>
              A best-effort estimate{freshDate ? <> as of {freshDate}</> : null}: the confirmed
              closing balance plus what you&rsquo;ve earned since (less ads), from the orders and
              payouts Clarity has pulled. The &ldquo;since&rdquo; part isn&rsquo;t settled by{' '}
              {platformLabel} yet and may shift — the latest days&rsquo; ads can still be in flight.
            </>
          ) : (
            <>
              A best-effort estimate{freshDate ? <> as of {freshDate}</> : null}, read from the
              latest statement Clarity has pulled — not a settled figure confirmed by {platformLabel},
              and it may lag what {platformLabel} shows.
            </>
          )
        ) : (
          <>Best-effort estimate{freshDate ? <>, as of {freshDate}</> : null}.</>
        )}
      </p>
    </section>
  );
}

/**
 * The inline-expanding "See full breakdown" for the payouts-panel card — the
 * exact orders + payout-ads the post-statement window figure is summed from
 * (clarity-daily-update Req 5.7). Mirrors the dashboard's drill-down; collapsed
 * by default, a real button with `aria-expanded`. Totals tie to the headline by
 * construction (same core window/filter).
 */
function PayoutBalanceDetails({
  breakdown,
  currency,
}: {
  breakdown: DerivedProfileSummary['estimatedBalance']['breakdown'];
  currency: string | undefined;
}) {
  const [open, setOpen] = useState(false);
  const fmt = (n: number) => money(n, currency);
  const orders = breakdown.orders;
  const ads = breakdown.ads;

  return (
    <div className="payouts__estimate-details">
      <button
        type="button"
        className="payouts__estimate-details-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Hide breakdown' : 'See full breakdown'}
        <span className="payouts__estimate-details-hint">
          {' '}
          ({orders.length} order{orders.length === 1 ? '' : 's'}
          {ads.length > 0 ? `, ${ads.length} ad charge${ads.length === 1 ? '' : 's'}` : ''})
        </span>
      </button>

      {open && (
        <div className="payouts__estimate-details-body">
          {orders.length > 0 && (
            <section aria-label="Orders earned since, not yet paid">
              <h4 className="payouts__estimate-details-head">Earned since, not yet paid</h4>
              <table className="data-table payouts__estimate-details-table">
                <thead>
                  <tr>
                    <th scope="col">Order</th>
                    <th scope="col">Placed</th>
                    <th scope="col" className="num">
                      Earnings
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr
                      key={o.id}
                      className={o.cancelled ? 'payouts__estimate-details-row--cancelled' : undefined}
                    >
                      <td>
                        {o.externalOrderId}
                        {o.cancelled ? (
                          <span className="payouts__estimate-details-tag"> cancelled</span>
                        ) : null}
                      </td>
                      <td className="num">{shortDate(o.timestamp)}</td>
                      <td className="num">{fmt(o.earnings)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {ads.length > 0 && (
            <section aria-label="Ads spent since the statement">
              <h4 className="payouts__estimate-details-head">Ads spent since (deducted)</h4>
              <table className="data-table payouts__estimate-details-table">
                <thead>
                  <tr>
                    <th scope="col">Charge</th>
                    <th scope="col">Period</th>
                    <th scope="col" className="num">
                      Ads
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {ads.map((a, i) => (
                    <tr key={`${a.kind}-${a.externalPayoutId ?? i}`}>
                      <td>{adsLineLabel(a, fmt)}</td>
                      <td className="num">
                        {shortDate(a.periodStart)} → {shortDate(a.periodEnd)}
                      </td>
                      <td className="num payouts__estimate-details-deduct">− {fmt(a.ads)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The "Next payout" line on the payouts card (clarity-payout-schedule Req 4.1,
 * 4.2) — mirrors the dashboard's block: the scheduled payout date, the coverage
 * window it settles, a best-effort amount (a slice of what's owed), and a "Payout
 * today" chip when it lands on the store's local today. A `monthly_closing` row
 * reads "settles the month".
 */
function UpcomingPayoutLine({
  upcoming,
  currency,
}: {
  upcoming: UpcomingPayout;
  currency: string | undefined;
}) {
  const fmt = (n: number) => money(n, currency);
  const isClosing = upcoming.type === 'monthly_closing';
  const cover =
    !isClosing && upcoming.coverageStart && upcoming.coverageEnd
      ? `covers ${shortDate(upcoming.coverageStart)} → ${shortDate(upcoming.coverageEnd)}`
      : 'settles the month';
  const notes = upcomingNotes(upcoming, fmt);
  return (
    <div className="payouts__estimate-upcoming" data-today={upcoming.dueToday ? 'yes' : 'no'}>
      <div className="payouts__estimate-upcoming-head">
        <span>Next payout · {shortDate(upcoming.payoutDate)}</span>
        {upcoming.dueToday && (
          <span className="payouts__estimate-today-chip">
            <span className="payouts__estimate-today-dot" aria-hidden="true" />
            Payout today
          </span>
        )}
      </div>
      <div className="payouts__estimate-upcoming-body">
        <span className="payouts__estimate-range">{cover}</span>
        {isClosing || upcoming.amount === null ? (
          <span className="payouts__estimate-upcoming-closing">settles the month</span>
        ) : (
          <span className="num payouts__estimate-upcoming-amt">
            ≈ {fmt(upcoming.amount)} <small>of what&rsquo;s owed</small>
          </span>
        )}
      </div>
      {!isClosing && upcoming.amount !== null && (notes.ads || notes.carried) && (
        <p className="payouts__estimate-upcoming-note">{[notes.ads, notes.carried].filter(Boolean).join(' · ')}</p>
      )}
      {notes.minimum && (
        <p className="payouts__estimate-upcoming-min" data-testid="upcoming-below-minimum">{notes.minimum}</p>
      )}
    </div>
  );
}

/**
 * Payouts, grouped by the month Talabat PAID them, each month answering one
 * question: does what Talabat says it paid that month (its monthly statement)
 * equal the payouts listed under it? A payout paid in June can cover late-May
 * orders, so a payout sits under the month it was paid, not the month it covers.
 * Payouts that can't be placed (no statement month, or no paid date yet) go under
 * "Other payouts". The headline estimated balance (Req 5.1) leads the panel.
 */
export function PayoutMatchPanel({
  payouts,
  estimate,
  currency,
  onOpenPayout,
  platformLabel = 'Talabat',
  careem,
}: {
  payouts: DerivedProfileSummary['payouts'];
  estimate: DerivedProfileSummary['estimatedBalance'];
  currency: string | undefined;
  onOpenPayout?: ((externalPayoutId: string) => void) | undefined;
  /** The owning platform's display name (e.g. 'Careem'); defaults to 'Talabat'. */
  platformLabel?: string;
  /**
   * The Careem-only extra block (clarity-careem-payout-status), carrying the
   * reported `liveBalance` + the `stillOwed` signal. Present only for a Careem
   * store; when `payouts.cycleChecks` is non-empty the panel renders the Careem
   * surface (weekly cycles grouped by month + the reported-balance headline)
   * instead of the Talabat SOA surface.
   */
  careem?: DerivedProfileSummary['careem'];
}) {
  // Careem surface: weekly cycles grouped by the month they were paid, with a
  // reported-balance headline — NOT the Talabat SOA statement machinery. Keyed
  // on `cycleChecks` being present so a Talabat payload is unaffected.
  if (payouts.cycleChecks.length > 0) {
    return (
      <CareemPayoutPanel
        payouts={payouts}
        careem={careem}
        currency={currency}
        onOpenPayout={onOpenPayout}
      />
    );
  }
  const checks = [...payouts.statementChecks].reverse(); // newest month first
  if (payouts.rows.length === 0 && checks.length === 0) {
    return (
      <section className="card payouts payouts--empty" aria-label="Payouts">
        <EstimatedBalanceCard estimate={estimate} currency={currency} platformLabel={platformLabel} />
        <SummaryGroupHead title="Payouts" />
        <p className="muted payouts__empty">
          No payouts settled for this period yet — they appear once {platformLabel} issues the settlement.
        </p>
      </section>
    );
  }
  const byId = new Map(payouts.rows.map((r) => [r.externalPayoutId, r] as const));
  const placed = new Set<string>();
  const months = checks.map((check) => {
    const rows = check.payouts.payoutIds
      .map((id) => byId.get(id))
      .filter((r): r is PayoutRowData => r !== undefined);
    for (const r of rows) placed.add(r.externalPayoutId);
    return { check, rows };
  });
  const others = payouts.rows.filter((r) => !placed.has(r.externalPayoutId));

  const failing = checks.filter(checkFails).length;
  const judged = checks.filter((c) => c.payouts.withinTolerance !== null).length;
  const badge =
    failing > 0
      ? { text: `${num(failing)} ${failing === 1 ? 'month doesn’t' : 'months don’t'} add up`, ok: false }
      : judged > 0
        ? { text: `Matches ${platformLabel}’s statements`, ok: true }
        : null;
  return (
    <section className="card payouts" aria-label="Payouts">
      <EstimatedBalanceCard estimate={estimate} currency={currency} platformLabel={platformLabel} />
      <header className="payouts__head">
        <SummaryGroupHead title="Payouts" />
        {badge && (
          <span className={`badge ${badge.ok ? 'badge-ok' : 'badge-warn'}`}>{badge.text}</span>
        )}
      </header>
      {months.map(({ check, rows }, i) => {
        // `months` is newest first, so the NEXT month is the previous entry.
        const next = i > 0 ? months[i - 1]!.check : null;
        return (
          <PayoutMonth
            key={check.periodStart}
            check={check}
            rows={rows}
            nextOpening={next?.cashFlow?.openingBalance ?? null}
            nextMonthName={next ? monthLabel(next.periodStart).split(' ')[0]! : null}
            isLatest={i === 0}
            currency={currency}
            platformLabel={platformLabel}
            onOpenPayout={onOpenPayout}
          />
        );
      })}
      {others.length > 0 && (
        <section className="payouts__month" aria-label="Other payouts">
          <header className="payouts__month-head">
            <h4>{months.length > 0 ? 'Other payouts' : 'Payouts'}</h4>
          </header>
          <PayoutList rows={others} currency={currency} platformLabel={platformLabel} onOpenPayout={onOpenPayout} />
        </section>
      )}
      <p className="muted payouts__note">
        A month matches when its payouts are within {STATEMENT_TOLERANCE_PCT}% of what {platformLabel}
        {'’s'} statement says it paid. A payout is listed under the month it was paid, which can be
        later than the orders it covers. Amounts are {platformLabel}{'’s'} reported figures.
      </p>
    </section>
  );
}

/** One month: the verdict in a sentence, the payouts behind it, and the statement's own lines. */
function PayoutMonth({
  check,
  rows,
  nextOpening,
  nextMonthName,
  isLatest,
  currency,
  platformLabel,
  onOpenPayout,
}: {
  check: StatementCheck;
  rows: readonly PayoutRowData[];
  /** The next month's statement opening balance, when that month is held. */
  nextOpening: number | null;
  nextMonthName: string | null;
  /** The most recent month: where money may genuinely still be owed. */
  isLatest: boolean;
  currency: string | undefined;
  /** The owning platform's display name (e.g. 'Careem' / 'Talabat'). */
  platformLabel: string;
  onOpenPayout?: ((externalPayoutId: string) => void) | undefined;
}) {
  const fmt = (n: number) => money(n, currency);
  const month = monthLabel(check.periodStart);
  const flow = check.cashFlow;
  const p = check.payouts;
  const count = `${num(p.count)} ${p.count === 1 ? 'payout' : 'payouts'}`;
  const fails = checkFails(check);
  const state: 'ok' | 'bad' | 'unknown' = fails ? 'bad' : p.withinTolerance === true ? 'ok' : 'unknown';

  // A payout paid in this month can cover EARLIER months' orders (Talabat pays
  // days after the window, and a month's last orders roll into the next month).
  // Say so: those are not what this month earned, they are its opening balance.
  const monthStart = Date.parse(check.periodStart);
  const earlierRows = rows.filter((r) => Date.parse(r.periodEnd) <= monthStart);
  const ownRows = rows.filter((r) => Date.parse(r.periodEnd) > monthStart);
  const earlierNet = Math.round(earlierRows.reduce((sum, r) => sum + r.reportedNetPayout, 0) * 100) / 100;
  const ownNet = Math.round(ownRows.reduce((sum, r) => sum + r.reportedNetPayout, 0) * 100) / 100;
  const earlierMonths = [...new Set(earlierRows.map((r) => monthLabel(r.periodStart).split(' ')[0]))];
  const earlierName = earlierMonths.length === 1 ? `${earlierMonths[0]}’s` : 'earlier months’';
  const isOpening = flow !== null && earlierRows.length > 0 && Math.abs(earlierNet - flow.openingBalance) <= 0.05;

  // What Talabat still owed at the END of the month, and where it ended up. The
  // month's last orders are paid by a payout issued after month end (`paidLater`),
  // or the balance carries into the next month's opening balance.
  const closing = flow !== null && flow.owedAtEnd > 0.005 ? flow.owedAtEnd : null;
  const near = (a: number, b: number) => Math.abs(a - b) <= Math.max(0.05, Math.abs(b) * 0.01);
  const laterNet = p.paidLater.net;
  const laterIds = p.paidLater.payoutIds.join(', ');
  const laterOn = p.paidLater.paidOn ? dayMonth(p.paidLater.paidOn) : null;
  type ClosingState = 'paid' | 'carried' | 'owed' | 'partial' | 'untraced';
  let closingState: ClosingState | null = null;
  if (closing !== null) {
    if (p.paidLater.count > 0 && near(laterNet, closing)) closingState = 'paid';
    else if (nextOpening !== null && near(nextOpening, closing)) closingState = 'carried';
    else if (!isLatest) closingState = 'untraced';
    else closingState = p.paidLater.count > 0 ? 'partial' : 'owed';
  }

  let verdict: ReactNode;
  if (flow === null) {
    verdict = `Re-derive to load what ${platformLabel}’s statement says it paid this month.`;
  } else if (!p.datesKnown) {
    verdict = 'Some payouts have no paid date yet, so they can’t be placed in a month. Re-derive to load them.';
  } else if (p.withinTolerance === true) {
    verdict = (
      <>
        {platformLabel} paid you <strong className="num">{fmt(flow.paid)}</strong> in {month}
        {earlierRows.length > 0 && ownRows.length > 0 ? (
          <>
            : <strong className="num">{fmt(earlierNet)}</strong> for {earlierName} orders
            {isOpening ? ' (the statement’s opening balance)' : ''} and{' '}
            <strong className="num">{fmt(ownNet)}</strong> for {month.split(' ')[0]}’s orders
          </>
        ) : null}
        . The {count} below add up to it
        {p.earlierPayouts > 0
          ? `, plus ${fmt(p.earlierPayouts)} paid to payouts from before the period Clarity pulled`
          : ''}
        .
      </>
    );
  } else if (p.difference !== null && p.differencePct !== null) {
    const less = p.difference < 0;
    verdict = (
      <>
        {platformLabel} says it paid you <strong className="num">{fmt(flow.paid)}</strong> in {month}, but
        the {count} below add up to <strong className="num">{fmt(p.net)}</strong>:{' '}
        <strong className="num">{fmt(Math.abs(p.difference))}</strong> {less ? 'less' : 'more'} (
        {pct(p.differencePct)}).{' '}
        {less
          ? 'A payout is probably missing.'
          : 'A payout may be dated in the wrong month.'}
      </>
    );
  }

  return (
    <section className="payouts__month" aria-label={`Payouts paid in ${month}`} data-state={state}>
      <header className="payouts__month-head">
        <h4>{month}</h4>
        <span className={`payouts__month-chip payouts__month-chip--${state}`}>
          {state === 'ok' ? '✓ Matches' : state === 'bad' ? 'Doesn’t add up' : 'Not checked yet'}
        </span>
      </header>
      <p className="payouts__month-verdict">{verdict}</p>
      {flow !== null && !flow.adds && (
        <p className="payouts__month-verdict">
          {platformLabel}’s own statement doesn’t add up either: {fmt(flow.openingBalance)} opening +{' '}
          {fmt(check.earned)} earned − {fmt(flow.paid)} paid is{' '}
          {fmt(flow.openingBalance + check.earned - flow.paid)}, but it shows{' '}
          {fmt(flow.owedAtEnd)} at the end.
        </p>
      )}
      {earlierRows.length > 0 && ownRows.length > 0 ? (
        <>
          <h5 className="payouts__month-sub">
            Paid in {month.split(' ')[0]} for {earlierName} orders{' '}
            <span className="num">{fmt(earlierNet)}</span>
          </h5>
          <PayoutList rows={earlierRows} currency={currency} platformLabel={platformLabel} onOpenPayout={onOpenPayout} />
          <h5 className="payouts__month-sub">
            Paid in {month.split(' ')[0]} for {month.split(' ')[0]}’s orders{' '}
            <span className="num">{fmt(ownNet)}</span>
          </h5>
          <PayoutList rows={ownRows} currency={currency} platformLabel={platformLabel} onOpenPayout={onOpenPayout} />
        </>
      ) : (
        rows.length > 0 && <PayoutList rows={rows} currency={currency} platformLabel={platformLabel} onOpenPayout={onOpenPayout} />
      )}
      {closing !== null && closingState !== null && (
        <div className="payouts__closing" data-state={closingState}>
          <p className="payouts__closing-line">
            <span>
              {closingState === 'owed' || closingState === 'partial'
                ? `${platformLabel} still owes you for ${month.split(' ')[0]}`
                : `At the end of ${month.split(' ')[0]} ${platformLabel} still owed you`}
            </span>
            <span className="num payouts__closing-amount">{fmt(closing)}</span>
          </p>
          <p className="payouts__closing-note">
            {closingState === 'paid' &&
              `It was paid on ${laterOn} in ${p.paidLater.count === 1 ? 'payout' : 'payouts'} ${laterIds}, so ${platformLabel} counts it in the next month.`}
            {closingState === 'carried' &&
              `It carried into ${nextMonthName} as that month’s opening balance and was paid there.`}
            {closingState === 'owed' &&
              'No payout for it has been paid yet, as far as Clarity can see. Re-derive to check for newer payouts.'}
            {closingState === 'partial' &&
              `${fmt(laterNet)} of it was paid on ${laterOn} (${laterIds}). The other ${fmt(closing - laterNet)} has not been paid out yet, as far as Clarity can see. Re-derive to check for newer payouts.`}
            {closingState === 'untraced' &&
              (p.paidLater.count > 0
                ? `${fmt(laterNet)} of it was paid on ${laterOn} (${laterIds}). The rest can’t be traced to a payout, because ${platformLabel}’s balances don’t carry forward cleanly for this month.`
                : `It can’t be traced to a payout, because ${platformLabel}’s balances don’t carry forward cleanly for this month.`)}
          </p>
        </div>
      )}
      <p className="payouts__month-foot muted">
        {month} earned <span className="num">{fmt(check.earned)}</span>
        {flow !== null && (
          <>. Paid can differ from earned because {platformLabel} pays days after the orders.</>
        )}
      </p>
    </section>
  );
}

/** A list of payouts with a small header strip. */
function PayoutList({
  rows,
  currency,
  platformLabel,
  onOpenPayout,
}: {
  rows: readonly PayoutRowData[];
  currency: string | undefined;
  /** The owning platform's display name (e.g. 'Careem' / 'Talabat'). */
  platformLabel: string;
  onOpenPayout?: ((externalPayoutId: string) => void) | undefined;
}) {
  return (
    <>
      <div className="payouts__cols" aria-hidden="true">
        <span />
        <span>Payout</span>
        <span>Paid on</span>
        <span>Net</span>
      </div>
      <ul className="payouts__list">
        {rows.map((r) => (
          <PayoutRow
            key={r.externalPayoutId}
            row={r}
            currency={currency}
            platformLabel={platformLabel}
            onOpenPayout={onOpenPayout}
          />
        ))}
      </ul>
    </>
  );
}

/** One expandable payout: id + the orders it covers, the day it was paid, its net, and Talabat's breakdown. */
function PayoutRow({
  row,
  currency,
  platformLabel,
  onOpenPayout,
}: {
  row: PayoutRowData;
  currency: string | undefined;
  /** The owning platform's display name (e.g. 'Careem' / 'Talabat'). */
  platformLabel: string;
  onOpenPayout?: ((externalPayoutId: string) => void) | undefined;
}) {
  const fmt = (n: number) => money(n, currency);
  const coverageKnown = row.expectedOrderCount !== null;
  // Careem rows carry the itemized adjustment ladder (clarity-careem-payout-
  // status Req 2): gross − partner-funded promo ⇒ order earnings, then each
  // deduction on its own line. Talabat rows leave `reportedOrderEarnings` null
  // and render the flat Gross/Commission/Fees/Ads/Net expand unchanged.
  const itemized = row.reportedOrderEarnings !== null;
  // Careem: the real Clarity-vs-Careem money check for this cycle, shown on the
  // collapsed row (the tick / the exact difference) and spelled out in the expand.
  const rec = row.reconciliation ?? null;
  const recLabel =
    rec === null
      ? null
      : rec.status === 'ok'
        ? '✓ Matches Careem'
        : `Differs by ${fmt(Math.abs(rec.difference))}`;
  const reconLines =
    rec === null ? null : (
      <>
        <div className="payouts__detail-row payouts__detail-row--recon">
          <dt>Careem reported</dt>
          <dd className="num">{fmt(rec.reportedNet)}</dd>
        </div>
        <div className="payouts__detail-row payouts__detail-row--recon">
          <dt>Clarity, rebuilt from your orders</dt>
          <dd className="num">{fmt(rec.clarityNet)}</dd>
        </div>
        {Math.abs(rec.carriedAmount) > 0.005 && (
          <div className="payouts__detail-row payouts__detail-note">
            <dt>includes carry-over from last cycle</dt>
            <dd className="num">
              {rec.carriedAmount < 0 ? '−' : '+'}
              {fmt(Math.abs(rec.carriedAmount))}
            </dd>
          </div>
        )}
        <div className="payouts__detail-row payouts__detail-row--recon">
          <dt>{rec.status === 'differs' ? 'Unexplained difference' : 'Difference'}</dt>
          <dd className="num">{fmt(rec.difference)}</dd>
        </div>
      </>
    );
  const ordersLine = (
    <div className="payouts__detail-row payouts__detail-note">
      <dt>Orders</dt>
      <dd className="num">
        {coverageKnown && row.expectedOrderCount !== row.linkedOrderCount
          ? `${platformLabel} counts ${num(row.expectedOrderCount)}, Clarity has ${num(row.linkedOrderCount)}`
          : num(row.linkedOrderCount)}
      </dd>
    </div>
  );
  return (
    <li className="payouts__item">
      <details>
        <summary className="payouts__summary">
          <span className="payouts__chevron" aria-hidden="true" />
          <span className="payouts__summary-main">
            <span className="payouts__summary-id num">{row.externalPayoutId}</span>
            <span className="payouts__summary-period">
              Covers orders {periodDates(row.periodStart, row.periodEnd) ?? ''}
            </span>
            {rec !== null && recLabel !== null && (
              <span className={`payouts__recon payouts__recon--${rec.status}`}>
                {recLabel}
                {row.expectedOrderCount !== null && row.expectedOrderCount !== row.linkedOrderCount
                  ? ` · ${num(row.expectedOrderCount)} vs ${num(row.linkedOrderCount)} orders`
                  : ''}
              </span>
            )}
          </span>
          <span className="payouts__paid">{row.paidAt ? dayMonth(row.paidAt.slice(0, 10)) : '—'}</span>
          <span className="payouts__summary-net num">{fmt(row.reportedNetPayout)}</span>
          {onOpenPayout && (
            <button
              type="button"
              className="link payouts__details"
              onClick={(e) => {
                // Inside a <summary>: stop the click from toggling the native
                // <details> disclosure; this navigates to the payout's own page.
                e.preventDefault();
                e.stopPropagation();
                onOpenPayout(row.externalPayoutId);
              }}
              aria-label={`Show details for payout ${row.externalPayoutId}`}
            >
              Details
            </button>
          )}
        </summary>
        {itemized ? (
          <dl className="payouts__detail">
            <div className="payouts__detail-row">
              <dt>Gross basket</dt>
              <dd className="num">{fmt(row.reportedGrossSales)}</dd>
            </div>
            <div className="payouts__detail-row payouts__detail-row--sub">
              <dt>Partner-funded promo</dt>
              <dd className="num">−{fmt(row.reportedPartnerFundedPromo)}</dd>
            </div>
            <div className="payouts__detail-row">
              <dt>Order earnings</dt>
              <dd className="num">{fmt(row.reportedOrderEarnings ?? 0)}</dd>
            </div>
            <div className="payouts__detail-row">
              <dt>Commission</dt>
              <dd className="num">−{fmt(row.reportedCommissions)}</dd>
            </div>
            <div className="payouts__detail-row">
              <dt>Payment gateway fee</dt>
              <dd className="num">−{fmt(row.reportedPaymentGatewayFee)}</dd>
            </div>
            <div className="payouts__detail-row">
              <dt>Careem Plus</dt>
              <dd className="num">−{fmt(row.reportedCareemPlus)}</dd>
            </div>
            <div className="payouts__detail-row">
              <dt>Bank transfer fee</dt>
              <dd className="num">−{fmt(row.reportedBankTransferFee)}</dd>
            </div>
            <div className="payouts__detail-row">
              <dt>Ads</dt>
              <dd className="num">−{fmt(row.reportedAds)}</dd>
            </div>
            {row.reportedPromoSharing > 0.005 && (
              <div className="payouts__detail-row">
                <dt>Promo sharing</dt>
                <dd className="num">−{fmt(row.reportedPromoSharing)}</dd>
              </div>
            )}
            {row.reportedClawback > 0.005 && (
              <div className="payouts__detail-row">
                <dt>Customer refunds &amp; quality issues</dt>
                <dd className="num">−{fmt(row.reportedClawback)}</dd>
              </div>
            )}
            {Math.abs(row.reportedCarryOver) > 0.005 && (
              <div className="payouts__detail-row">
                <dt>Carried over from last cycle</dt>
                <dd className="num">
                  {row.reportedCarryOver < 0 ? '−' : '+'}
                  {fmt(Math.abs(row.reportedCarryOver))}
                </dd>
              </div>
            )}
            <div className="payouts__detail-row payouts__detail-row--net">
              <dt>Net payout</dt>
              <dd className="num">{fmt(row.reportedNetPayout)}</dd>
            </div>
            {ordersLine}
            {reconLines}
          </dl>
        ) : row.talabatBreakdown ? (
          // Talabat itemized ladder — the SAME line-by-line treatment as the
          // Careem cycle expand, from Talabat's own reported breakdown groups. A
          // zero group is omitted so the ladder stays tight; `additionalSales` is
          // money Talabat paid IN (shown as a positive), every other line is a
          // deduction (shown negative). The lines don't arithmetically sum to the
          // net on their own (Talabat's net nets account-level balance/loans too),
          // so the net is shown as the reported figure, not a running total.
          (() => {
            const b = row.talabatBreakdown;
            const deduction = (label: string, amount: number) =>
              amount > 0.005 ? (
                <div className="payouts__detail-row">
                  <dt>{label}</dt>
                  <dd className="num">−{fmt(amount)}</dd>
                </div>
              ) : null;
            return (
              <dl className="payouts__detail">
                <div className="payouts__detail-row">
                  <dt>Gross sales</dt>
                  <dd className="num">{fmt(row.reportedGrossSales)}</dd>
                </div>
                {b.additionalSales > 0.005 && (
                  <div className="payouts__detail-row payouts__detail-row--sub">
                    <dt>Delivery, packaging &amp; tips paid in</dt>
                    <dd className="num">+{fmt(b.additionalSales)}</dd>
                  </div>
                )}
                {deduction('Commission', b.commission)}
                {deduction('Payment & other fees', b.additionalFees)}
                {deduction('Marketing / Ads', b.marketing)}
                {deduction('Customer refunds & quality issues', b.customerRefundCharges)}
                {deduction('Customer fees', b.customerFees)}
                {deduction('Operational & waiting charges', b.waitTimeFee)}
                {deduction('Tax charges', b.taxCharges)}
                {deduction('Loans & recovery', b.loans)}
                <div className="payouts__detail-row payouts__detail-row--net">
                  <dt>Net payout</dt>
                  <dd className="num">{fmt(row.reportedNetPayout)}</dd>
                </div>
                {ordersLine}
              </dl>
            );
          })()
        ) : (
          <dl className="payouts__detail">
            <div className="payouts__detail-row">
              <dt>Gross sales</dt>
              <dd className="num">{fmt(row.reportedGrossSales)}</dd>
            </div>
            <div className="payouts__detail-row">
              <dt>Commission</dt>
              <dd className="num">{fmt(row.reportedCommissions)}</dd>
            </div>
            <div className="payouts__detail-row">
              <dt>Fees</dt>
              <dd className="num">{fmt(row.reportedFees)}</dd>
            </div>
            <div className="payouts__detail-row">
              <dt>Ads</dt>
              <dd className="num">{fmt(row.reportedAds)}</dd>
            </div>
            <div className="payouts__detail-row payouts__detail-row--net">
              <dt>Net payout</dt>
              <dd className="num">{fmt(row.reportedNetPayout)}</dd>
            </div>
            {ordersLine}
          </dl>
        )}
      </details>
    </li>
  );
}

/**
 * The Careem payouts surface (clarity-careem-payout-status): weekly billing
 * cycles grouped by the month they were paid, with Careem's REPORTED balance as
 * the headline (not the Talabat statement-anchored estimate). One verdict per
 * month from `payouts.cycleChecks`; money is the primary signal, a coverage gap
 * is surfaced as "Check orders" without failing the month.
 */
function CareemPayoutPanel({
  payouts,
  careem,
  currency,
  onOpenPayout,
}: {
  payouts: DerivedProfileSummary['payouts'];
  careem: DerivedProfileSummary['careem'];
  currency: string | undefined;
  onOpenPayout?: ((externalPayoutId: string) => void) | undefined;
}) {
  const byId = new Map(payouts.rows.map((r) => [r.externalPayoutId, r] as const));
  // Newest month first, like the Talabat panel.
  const months = [...payouts.cycleChecks]
    .sort((a, b) => Date.parse(b.periodStart) - Date.parse(a.periodStart))
    .map((check) => ({
      check,
      rows: check.cycles.payoutIds
        .map((id) => byId.get(id))
        .filter((r): r is PayoutRowData => r !== undefined),
    }));
  const anyBad = payouts.cycleChecks.some((c) => c.state === 'bad');
  const badge = anyBad
    ? { text: 'A month doesn’t add up', ok: false }
    : { text: 'Matches Careem’s payouts', ok: true };

  return (
    <section className="card payouts" aria-label="Payouts">
      <CareemBalanceCard careem={careem} currency={currency} />
      <header className="payouts__head">
        <SummaryGroupHead title="Payouts" />
        <span className={`badge ${badge.ok ? 'badge-ok' : 'badge-warn'}`}>{badge.text}</span>
      </header>
      {months.map(({ check, rows }) => (
        <CareemCycleMonth
          key={check.periodStart}
          check={check}
          rows={rows}
          currency={currency}
          onOpenPayout={onOpenPayout}
        />
      ))}
      <p className="muted payouts__note">
        Careem pays weekly. Each payout is one billing cycle, grouped under the month it was paid. A
        month matches when every cycle reconciles — the orders Clarity has explain the cycle balance
        once Careem’s commission, fees, Careem Plus and ads are taken out — and the order count
        matches the cycle’s. Amounts are Careem’s reported figures.
      </p>
    </section>
  );
}

/** Careem's REPORTED live balance headline + the order-derived "still owed" line. */
function CareemBalanceCard({
  careem,
  currency,
}: {
  careem: DerivedProfileSummary['careem'];
  currency: string | undefined;
}) {
  const fmt = (n: number) => money(n, currency);
  const balance = careem?.liveBalance ?? null;
  const stillOwed = careem?.stillOwed ?? null;
  const adjustmentEntries = careem?.adjustmentEntries ?? [];
  return (
    <section className="payouts__estimate" data-state="careem" aria-label="Balance Careem can pay out">
      <SummaryGroupHead title="Balance Careem can pay out" />
      {balance ? (
        <>
          <p className="payouts__estimate-line">
            <span>Balance now</span>
            <span className="num payouts__estimate-amount">{fmt(balance.total)}</span>
          </p>
          <dl className="payouts__estimate-parts">
            <div className="payouts__estimate-part">
              <dt>Next payout</dt>
              <dd>end of this week’s cycle</dd>
            </div>
            {balance.underProcess > 0.005 && (
              <div className="payouts__estimate-part">
                <dt>Under process</dt>
                <dd className="num">{fmt(balance.underProcess)}</dd>
              </div>
            )}
          </dl>
          <p className="payouts__estimate-note muted">Reported by Careem — not an estimate.</p>
        </>
      ) : (
        <p className="payouts__estimate-reason">
          Careem’s balance isn’t available right now — re-sync to read it.
        </p>
      )}
      {stillOwed !== null && stillOwed > 0.005 && (
        <p className="payouts__estimate-note" data-state="owed">
          Clarity sees orders Careem hasn’t paid for yet — about{' '}
          <span className="num">{fmt(stillOwed)}</span> still owed beyond the balance above.
        </p>
      )}
      {adjustmentEntries.length > 0 && (
        <div className="payouts__adjustments">
          <h5 className="payouts__adjustments-head">Adjustments outside the weekly payouts</h5>
          <p className="muted payouts__adjustments-note">
            Charges Careem made at the account level (not in any single weekly cycle). Recheck each
            against your Careem portal by its date.
          </p>
          <table className="payouts__adjustments-table">
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Charge</th>
                <th scope="col" className="num">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {adjustmentEntries.map((e, i) => (
                <tr key={`${e.category}-${e.date ?? 'x'}-${i}`}>
                  <td className="num">{e.date ?? '—'}</td>
                  <td>{humanizeCareemCategory(e.category)}{e.description ? ` · ${e.description}` : ''}</td>
                  <td className="num">{fmt(e.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** One month of Careem weekly cycles: the verdict chip, a short sentence, the cycles. */
function CareemCycleMonth({
  check,
  rows,
  currency,
  onOpenPayout,
}: {
  check: DerivedProfileSummary['payouts']['cycleChecks'][number];
  rows: readonly PayoutRowData[];
  currency: string | undefined;
  onOpenPayout?: ((externalPayoutId: string) => void) | undefined;
}) {
  const fmt = (n: number) => money(n, currency);
  const month = monthLabel(check.periodStart);
  const state = check.state === 'ok' ? 'ok' : check.state === 'bad' ? 'bad' : 'unknown';
  const chip =
    check.state === 'ok' ? '✓ Matches' : check.state === 'bad' ? 'Doesn’t add up' : 'Check orders';
  const n = check.cycles.count;
  const differing = rows.filter((r) => r.reconciliation?.status === 'differs');
  const differingTotal = differing.reduce((s, r) => s + Math.abs(r.reconciliation?.difference ?? 0), 0);
  const monthName = month.split(' ')[0];
  const verdict =
    check.state === 'ok'
      ? `Careem paid you ${fmt(check.cycles.net)} across ${num(n)} weekly ${n === 1 ? 'payout' : 'payouts'} in ${monthName}. Clarity rebuilt every one from your orders and they match.`
      : check.state === 'check_orders'
        ? `The ${num(n)} ${n === 1 ? 'payout' : 'payouts'} add up to ${fmt(check.cycles.net)} and the money matches, but on at least one cycle Careem counted a different number of orders than Clarity has.`
        : `Careem paid you ${fmt(check.cycles.net)} across ${num(n)} ${n === 1 ? 'payout' : 'payouts'} in ${monthName}. ${num(differing.length)} of them ${differing.length === 1 ? 'differs' : 'differ'} from what Clarity rebuilds from your orders (${fmt(differingTotal)} in total, see each payout) — most likely an adjustment Careem made that isn’t on the order lines.`;

  // This panel is a CASH view: it groups cycles by the month Careem PAID them,
  // matching Careem's own portal — a different lens from the "Where your money
  // goes" breakdown (an EARNINGS view by the month orders were PLACED). A cycle
  // paid this month can cover the PRIOR month's orders (e.g. "Aug 24 – 31", paid
  // Sep 1, shown here under September). Split the list the SAME way the Talabat
  // month card does — "Paid in {month} for {prior}'s orders" vs "for {month}'s
  // orders" — so a cross-month payout is named, not silently lumped. A cycle's
  // orders belong to a prior month when its period ENDS at/before this paid
  // month's start.
  const monthStart = Date.parse(check.periodStart);
  const earlierRows = rows.filter((r) => Date.parse(r.periodEnd) <= monthStart);
  const ownRows = rows.filter((r) => Date.parse(r.periodEnd) > monthStart);
  const r2 = (n: number) => Math.round(n * 100) / 100;
  const earlierNet = r2(earlierRows.reduce((sum, r) => sum + r.reportedNetPayout, 0));
  const ownNet = r2(ownRows.reduce((sum, r) => sum + r.reportedNetPayout, 0));
  const earlierMonths = [...new Set(earlierRows.map((r) => monthLabel(r.periodStart).split(' ')[0]))];
  const earlierName = earlierMonths.length === 1 ? `${earlierMonths[0]}’s` : 'earlier months’';
  const split = earlierRows.length > 0 && ownRows.length > 0;
  return (
    <section className="payouts__month" aria-label={`Payouts paid in ${month}`} data-state={state}>
      <header className="payouts__month-head">
        <h4>{month}</h4>
        <span className={`payouts__month-chip payouts__month-chip--${state}`}>{chip}</span>
      </header>
      <p className="payouts__month-verdict">{verdict}</p>
      <p className="payouts__month-note muted">
        This is when Careem <strong>paid</strong> you, grouped by pay date — a cash view that matches
        Careem’s portal. Your <em>earnings</em> for a month (the “Where your money goes” breakdown)
        are grouped by when orders were <strong>placed</strong>, so the two can differ on a payout
        that straddles a month edge.
      </p>
      {split ? (
        <>
          <h5 className="payouts__month-sub">
            Paid in {monthName} for {earlierName} orders <span className="num">{fmt(earlierNet)}</span>
          </h5>
          <PayoutList rows={earlierRows} currency={currency} platformLabel="Careem" onOpenPayout={onOpenPayout} />
          <h5 className="payouts__month-sub">
            Paid in {monthName} for {monthName}’s orders <span className="num">{fmt(ownNet)}</span>
          </h5>
          <PayoutList rows={ownRows} currency={currency} platformLabel="Careem" onOpenPayout={onOpenPayout} />
        </>
      ) : (
        rows.length > 0 && (
          <PayoutList rows={rows} currency={currency} platformLabel="Careem" onOpenPayout={onOpenPayout} />
        )
      )}
    </section>
  );
}
